import { useState } from "react";
import { Document } from "../../../model/FileManager";
import { useFileManager } from "../hooks/useFileManager";

interface FileListProps {
    files: Document[];
    searchQuery: string;
    isLoading?: boolean;
    pagination?: {
        limit: number;
        offset: number;
        total: number;
    };
    tenantId: string,
    onPageChange?: (offset: number) => void;
    onDelete?: (filename: string) => void;
}

export const FileList: React.FC<FileListProps> = ({
    files = [],
    searchQuery = "",
    isLoading = false,
    pagination,
    tenantId,
    onPageChange,
    onDelete,
}) => {
    const [isOpening, setIsOpening] = useState<Record<string, boolean>>({});
    const { openFile } = useFileManager();

    const handleFileOpen = async (file: Document, e: React.MouseEvent) => {
        e.preventDefault();
        setIsOpening((prev) => ({ ...prev, [file.filename]: true }));

        try {
            const presignedUrl = await openFile(tenantId, file.filename);

            if (!presignedUrl) {
                console.error("No URL returned");
                return;
            }
            window.open(
                String(presignedUrl).toString(),
                "_blank",
                "noopener,noreferrer"
            );
        } catch (error) {
            console.error("Error opening file:", error);
        } finally {
            setIsOpening((prev) => ({ ...prev, [file.filename]: false }));
        }
    };

    if (!isLoading && (!files || files.length === 0)) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <svg
                    className="w-16 h-16 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                </svg>
                <p className="text-lg font-medium">No files found</p>
                <p className="text-sm">
                    Drop files here or use the upload button to get started
                </p>
            </div>
        );
    }

    const filteredFiles = files.filter((file) => {
        if (!file || !file.filename) return false;
        return file.filename
            .toLowerCase()
            .includes((searchQuery || "").toLowerCase());
    });

    if (!isLoading && searchQuery && filteredFiles.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <svg
                    className="w-16 h-16 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                </svg>
                <p className="text-lg font-medium">No matches found</p>
                <p className="text-sm">Try adjusting your search term</p>
            </div>
        );
    }
    
    return (
        <div className="flex-1 overflow-auto">
            <div className="flex font-semibold text-gray-600 p-2 border-b">
                <div className="flex-1">Name</div>
                <div className="w-16"></div>
            </div>

            <div className="divide-y divide-gray-200">
                {filteredFiles.map((file) => (
                    <div
                        key={file.filename}
                        className="flex items-center p-3 hover:bg-gray-50 transition-colors duration-150"
                    >
                        <div className="flex-1 truncate">
                            <span
                                className="cursor-pointer hover:text-blue-600 hover:underline flex items-center"
                                onClick={(e) => handleFileOpen(file, e)}
                            >
                                {/* PDF Icon */}
                                <svg
                                    className="w-5 h-5 mr-2 text-gray-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                </svg>

                                {isOpening[file.filename] ? (
                                    <span className="text-gray-500">
                                        Opening...
                                    </span>
                                ) : (
                                    file.filename
                                )}
                            </span>
                        </div>

                        <div className="w-16 flex justify-center">
                            <button
                                onClick={() => onDelete?.(file.filename)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors duration-150"
                                title="Delete file"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination */}
            {pagination && (
                <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                        <button
                            onClick={() =>
                                onPageChange?.(
                                    Math.max(
                                        0,
                                        pagination.offset - pagination.limit
                                    )
                                )
                            }
                            disabled={pagination.offset === 0}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() =>
                                onPageChange?.(
                                    pagination.offset + pagination.limit
                                )
                            }
                            disabled={
                                pagination.offset + pagination.limit >=
                                pagination.total
                            }
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                        >
                            Next
                        </button>
                    </div>

                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing{" "}
                                <span className="font-medium">
                                    {pagination.total > 0
                                        ? pagination.offset + 1
                                        : 0}
                                </span>{" "}
                                -{" "}
                                <span className="font-medium">
                                    {Math.min(
                                        pagination.offset + pagination.limit,
                                        pagination.total
                                    )}
                                </span>{" "}
                                of{" "}
                                <span className="font-medium">
                                    {pagination.total}
                                </span>{" "}
                                results
                            </p>
                        </div>
                        <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <button
                                    onClick={() =>
                                        onPageChange?.(
                                            Math.max(
                                                0,
                                                pagination.offset -
                                                    pagination.limit
                                            )
                                        )
                                    }
                                    disabled={pagination.offset === 0}
                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                    <span className="sr-only">Previous</span>←
                                </button>
                                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    Page{" "}
                                    {Math.floor(
                                        pagination.offset / pagination.limit
                                    ) + 1}
                                    {" / "}
                                    {Math.ceil(
                                        pagination.total / pagination.limit
                                    ) || 1}
                                </span>
                                <button
                                    onClick={() =>
                                        onPageChange?.(
                                            pagination.offset + pagination.limit
                                        )
                                    }
                                    disabled={
                                        pagination.offset + pagination.limit >=
                                        pagination.total
                                    }
                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                    <span className="sr-only">Next</span>→
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
